import React, { useState } from "react"
import { db } from "../../../utils/firebase"
import firebase from "firebase"
import moment from "moment"

// UI imports
import Button from "../../../components/ui/button/button"
import Table from "../../../components/structure/table/table"
import Datepicker from "../../../components/ui/datepicker/datepicker"

// Export helper
import { exportSheetsToExcel } from "../_utils/export"

/**
 * Batch size for reading booking subcollections. Matches the concurrency used by
 * the itemised-stats page to avoid Firestore "too many outstanding requests".
 */
const BATCH_SIZE = 10

/**
 * "Booking suppliers" tab - exports the hotels, courses and transfers that are
 * attached to bookings within a chosen travel-date window. For each supplier we
 * show the name, ID and contact email alongside the number of distinct bookings
 * it is assigned against, sorted most-used first, and let the user download all
 * three lists as a single workbook (one tab each).
 */
export default function BookingSuppliers() {
	// Date range (stored as milliseconds, the value the Datepicker round-trips).
	// Default to the next 24 months - the primary question this tab answers.
	const [startDate, setStartDate] = useState(moment().startOf("day").valueOf())
	const [endDate, setEndDate] = useState(moment().add(24, "months").endOf("day").valueOf())

	// Loading / progress state
	const [loading, setLoading] = useState(false)
	const [progress, setProgress] = useState({ done: 0, total: 0 })

	// Report state
	const [generated, setGenerated] = useState(false)
	const [scanned, setScanned] = useState(0)
	const [rangeLabel, setRangeLabel] = useState("")

	// Result lists - each item: { id, name, email, bookings }
	const [hotelsList, setHotelsList] = useState([])
	const [coursesList, setCoursesList] = useState([])
	const [transfersList, setTransfersList] = useState([])

	/**
	 * Apply a quick preset that runs from the start of today forward N months.
	 */
	const applyPreset = (months) => {
		setStartDate(moment().startOf("day").valueOf())
		setEndDate(moment().add(months, "months").endOf("day").valueOf())
	}

	/**
	 * Turn a counts map ({ id: bookingCount }) into a sorted list of rows,
	 * resolving each supplier's name/email from its lookup collection. Suppliers
	 * whose ID no longer exists in the lookup are still listed (flagged) so the
	 * usage is never silently dropped.
	 */
	const buildList = (counts, lookup) => {
		return Object.entries(counts)
			.map(([id, bookings]) => {
				const supplier = lookup[id] || {}
				return {
					id,
					name: supplier.name || "(Unknown / removed supplier)",
					email: supplier.email || "",
					bookings,
				}
			})
			.sort((a, b) => b.bookings - a.bookings)
	}

	/**
	 * Generate the report for the chosen travel-date window.
	 */
	const generateReport = async () => {
		if (!startDate || !endDate || loading) return

		setLoading(true)
		setGenerated(false)
		setProgress({ done: 0, total: 0 })

		try {
			// Build the inclusive travel-date window as Firestore timestamps
			const startMillis = moment(startDate).startOf("day").valueOf()
			const endMillis = moment(endDate).endOf("day").valueOf()
			const startTs = firebase.firestore.Timestamp.fromMillis(startMillis)
			const endTs = firebase.firestore.Timestamp.fromMillis(endMillis)

			// Fetch the supplier lookup collections and the in-range active
			// bookings in parallel. Bookings are filtered on their travel date
			// (check_in) and exclude soft-deleted records.
			const [hotelsSnap, coursesSnap, transfersSnap, bookingsSnap] = await Promise.all([
				db.collection("hotels").get(),
				db.collection("courses").get(),
				db.collection("transfers").get(),
				db.collection("bookings").where("check_in", ">=", startTs).where("check_in", "<=", endTs).where("removed", "==", false).orderBy("check_in").get(),
			])

			// Build id -> { name, email } lookup maps
			const hotelsLookup = {}
			hotelsSnap.forEach((doc) => (hotelsLookup[doc.id] = doc.data()))
			const coursesLookup = {}
			coursesSnap.forEach((doc) => (coursesLookup[doc.id] = doc.data()))
			const transfersLookup = {}
			transfersSnap.forEach((doc) => (transfersLookup[doc.id] = doc.data()))

			const bookingDocs = bookingsSnap.docs
			setScanned(bookingDocs.length)
			setProgress({ done: 0, total: bookingDocs.length })

			// Accumulators: supplier id -> number of distinct bookings using it
			const hotelCounts = {}
			const courseCounts = {}
			const transferCounts = {}

			// Transfers live on a single field of the booking document, so they
			// can be counted without touching any subcollection.
			bookingDocs.forEach((doc) => {
				const transferId = doc.data().transfer
				if (transferId) {
					transferCounts[transferId] = (transferCounts[transferId] || 0) + 1
				}
			})

			// Hotels and courses live in per-booking subcollections. Read them in
			// batches and count each supplier once per booking (a hotel used for
			// two room blocks on one booking still counts as a single booking).
			const bookingIds = bookingDocs.map((doc) => doc.id)

			for (let i = 0; i < bookingIds.length; i += BATCH_SIZE) {
				const batch = bookingIds.slice(i, i + BATCH_SIZE)

				const batchResults = await Promise.all(
					batch.map(async (bookingId) => {
						const [hotelItems, roundItems] = await Promise.all([
							db
								.collection(`bookings/${bookingId}/hotels`)
								.get()
								.catch(() => ({ docs: [] })),
							db
								.collection(`bookings/${bookingId}/rounds`)
								.get()
								.catch(() => ({ docs: [] })),
						])

						// Distinct suppliers used on this booking
						const hotelIds = new Set()
						hotelItems.docs.forEach((item) => {
							const resortID = item.data().resortID
							if (resortID) hotelIds.add(resortID)
						})

						const courseIds = new Set()
						roundItems.docs.forEach((item) => {
							const courseID = item.data().courseID
							if (courseID) courseIds.add(courseID)
						})

						return { hotelIds, courseIds }
					})
				)

				batchResults.forEach(({ hotelIds, courseIds }) => {
					hotelIds.forEach((id) => (hotelCounts[id] = (hotelCounts[id] || 0) + 1))
					courseIds.forEach((id) => (courseCounts[id] = (courseCounts[id] || 0) + 1))
				})

				setProgress({ done: Math.min(i + BATCH_SIZE, bookingIds.length), total: bookingIds.length })
			}

			// Build the sorted result lists
			setHotelsList(buildList(hotelCounts, hotelsLookup))
			setCoursesList(buildList(courseCounts, coursesLookup))
			setTransfersList(buildList(transferCounts, transfersLookup))

			setRangeLabel(`${moment(startMillis).format("D MMM YYYY")} - ${moment(endMillis).format("D MMM YYYY")}`)
			setGenerated(true)
		} catch (error) {
			console.error("Error generating booking suppliers report:", error)
		} finally {
			setLoading(false)
		}
	}

	// Section config so the three tables render from a single template
	const sections = [
		{ key: "Hotels", noun: "hotel", list: hotelsList },
		{ key: "Courses", noun: "course", list: coursesList },
		{ key: "Transfers", noun: "transfer", list: transfersList },
	]

	// Total rows across all three lists (drives the export button state)
	const totalRows = hotelsList.length + coursesList.length + transfersList.length

	/**
	 * Export all three lists into a single XLSX workbook - one tab per list
	 * (Hotels / Courses / Transfers). Object keys become the column headers.
	 */
	const exportAll = () => {
		if (!generated || totalRows === 0) return

		const headers = ["Name", "ID", "Contact Email", "Bookings"]
		const toRows = (list) =>
			list.map((item) => ({
				Name: item.name,
				ID: item.id,
				"Contact Email": item.email,
				Bookings: item.bookings,
			}))

		const sheets = sections.map(({ key, list }) => ({ name: key, rows: toRows(list), headers }))

		const fileRange = `${moment(startDate).format("YYYY-MM-DD")}_to_${moment(endDate).format("YYYY-MM-DD")}`
		exportSheetsToExcel(sheets, `booking_suppliers_${fileRange}`)
	}

	return (
		<>
			<p className="db-exports-intro">
				Export the hotels, courses and transfers attached to bookings within a travel-date window. Each list shows the supplier name, ID and contact email alongside the number of bookings it is
				assigned against, sorted most-used first. Generating the lists lets you download them as a single spreadsheet with a tab each for hotels, courses and transfers.
			</p>

			<div className="page-filters db-exports-filters">
				<Datepicker
					value={startDate}
					onSelect={(date) => setStartDate(date)}
					placeholder="From (travel date)"
				/>

				<Datepicker
					value={endDate}
					onSelect={(date) => setEndDate(date)}
					placeholder="To (travel date)"
				/>

				<div className="db-exports-presets">
					<Button
						filters={true}
						small={true}
						label="Next 12 months"
						onClick={() => applyPreset(12)}
					/>
					<Button
						filters={true}
						small={true}
						label="Next 24 months"
						onClick={() => applyPreset(24)}
					/>
				</div>

				<Button
					filters={true}
					disabled={!startDate || !endDate}
					label="Generate lists"
					loadingText="Generating..."
					loading={loading}
					onClick={() => generateReport()}
				/>

				<Button
					filters={true}
					disabled={!generated || totalRows === 0}
					label="Export to XLSX (3 tabs)"
					onClick={() => exportAll()}
				/>
			</div>

			{/* Progress / summary line */}
			{loading && (
				<p className="db-exports-summary">
					Scanning bookings... {progress.done}
					{progress.total > 0 ? ` / ${progress.total}` : ""}
				</p>
			)}

			{!loading && generated && (
				<p className="db-exports-summary">
					Scanned <strong>{scanned.toLocaleString()}</strong> active booking{scanned === 1 ? "" : "s"} travelling between <strong>{rangeLabel}</strong>.
				</p>
			)}

			{/* Result sections */}
			{generated &&
				sections.map(({ key, noun, list }) => (
					<div
						key={key}
						className="db-exports-section">
						<div className="db-exports-section-header">
							<h2>
								{key} <span className="db-exports-count">({list.length.toLocaleString()} {noun}{list.length === 1 ? "" : "s"})</span>
							</h2>
						</div>

						<Table
							className="db-exports-table"
							headings={["Name", "ID", "Contact email", "Bookings"]}
							noResults={list.length === 0}
							noResultsMessage={`No ${noun}s attached to bookings in this window`}>
							{list.map((item) => (
								<tr key={item.id}>
									<td>{item.name}</td>
									<td className="db-exports-id">{item.id}</td>
									<td>{item.email || <span className="db-exports-muted">-</span>}</td>
									<td>{item.bookings.toLocaleString()}</td>
								</tr>
							))}
						</Table>
					</div>
				))}
		</>
	)
}
